import * as wjcCore from '@mescius/wijmo';
import {AfterViewInit, Directive, Host, Input} from '@angular/core';
import {WjFlexGrid} from '@mescius/wijmo.angular2.grid';

/**
 * すべての wj-flex-grid の列幅を内容 (最大文字数) に合わせて自動調整するディレクティブ。
 *
 * selector を要素セレクタ `wj-flex-grid` にしているため、テンプレートを個別に
 * 変更しなくても全グリッド (39 箇所) に自動適用される。新規グリッドも対象になる。
 *
 * WjFlexGrid は wijmo の FlexGrid を継承しているので、ホスト要素の同名コンポーネントを
 * DI で受け取り、autoSizeColumns() で列幅をヘッダ・セル内容の最大幅に合わせる。
 * さらにデータ型別の最低幅 (Date=100 / Number=80 / String=120) を下限として補正する。
 *
 * 発火タイミングが肝:
 *  - 初回は「グリッド描画・データ流し込み完了後」でないと測定できないため、
 *    ngAfterViewInit の次フレーム (setTimeout 0) で実行する。ngOnInit で即時に
 *    呼ぶと描画前で空振りする。
 *  - itemsSource が非同期ロード (OrientDBCollectionView 等) の場合や、以後の
 *    データ差替え・ソート/フィルタに追従するため loadedRows (wijmo コアの Event)
 *    でも再調整する。Angular ラッパの @Output initialized (subscribe) とは別物。
 *  - updatedView は仮想化グリッドのスクロール毎に発火し列幅がちらつく・重くなるため使わない。
 */
@Directive({
    standalone: false,
    selector: 'wj-flex-grid',
})
export class FlexGridAutoSizeDirective implements AfterViewInit {
    /**
     * false で自動調整しない (テンプレートの [width] をそのまま使う)。
     * 入力用の空グリッドなど、内容に合わせると最低幅まで縮んでしまう画面で使う。
     */
    @Input() apAutoSize = true;

    constructor(@Host() private grid: WjFlexGrid) {
    }

    ngAfterViewInit(): void {
        if (!this.apAutoSize) {
            return;
        }
        const fit = () => this.autoSize();
        // データ差替え・ソート/フィルタ後の行生成時に列幅を再計算する。
        this.grid.loadedRows.addHandler(fit);
        // 描画完了後 (事前ロード済み CollectionView 含む) に一度確実に実行する。
        setTimeout(fit, 0);
    }

    private autoSize(): void {
        try {
            // 引数なしで全列をヘッダ・セル内容の最大幅に合わせる。
            this.grid.autoSizeColumns();
            // 内容が狭くても列種別ごとの最低幅を割らないように補正する。
            for (const col of this.grid.columns) {
                const min = FlexGridAutoSizeDirective.minWidthFor(col);
                if (min != null && col.renderWidth < min) {
                    col.width = min;
                }
            }
        } catch (e) {
            // グリッド未描画・列未定義などのタイミングでは黙って無視する。
        }
    }

    /**
     * 列種別ごとの最低幅 (px)。
     * Number=80 / Date=100。Boolean はチェックボックス等なので下限なし。
     * String はもちろん、LINK・DataMap・型未確定 (買主/カテゴリ等) や、テキストリンクの
     * テンプレート列 (操作の編集/削除等) も含め、それ以外はすべて 120 を下限にする。
     */
    private static minWidthFor(col: {dataType: wjcCore.DataType}): number | null {
        switch (col.dataType) {
            case wjcCore.DataType.Boolean:
                return null;
            case wjcCore.DataType.Number:
                return 80;
            case wjcCore.DataType.Date:
                return 100;
            default:
                return 120;
        }
    }
}
